/**
 * Membership cadence pricing — resolves the annual price of each cadence from
 * the real `membership_tiers` data (source of truth is Stripe, refreshed by the
 * get-products edge function into `annual_amount_chf`). Falls back to defaults
 * only when the Stripe-refreshed amount is absent (e.g. local dev without
 * STRIPE_PRODUCT_READ) — the defaults mirror `TIER_DEFAULTS` in
 * MembershipDetailsModal, keep the two in sync.
 */
import type { MembershipTier } from "@/hooks/createProductsQueryFn";

/** The membership cadence options: baseline (1×) / twice (2×) / quarterly (4×). */
export type RecommendedPlan = "baseline" | "twice" | "quarterly";

/** Cadence plan id (UI) → `membership_tiers` row id (Stripe-backed). */
export const CADENCE_TIER_ID: Record<RecommendedPlan, string> = {
  baseline: "once",
  twice: "twice",
  quarterly: "four",
};

/** Cadence plan → ahead_mono membership `planSlug` (PLAN_SLUG_MAP in
 *  ahead_mono membershipProducts.ts). Sent to create-blood-checkout, which
 *  forwards it to mono's provision-from-subscription endpoint so the right
 *  entitlements (follow-up tests) are provisioned. base_plus_{N follow-ups}. */
export const CADENCE_PLAN_SLUG: Record<RecommendedPlan, string> = {
  baseline: "base_plus_0",
  twice: "base_plus_1",
  quarterly: "base_plus_3",
};

/** Cadence plan → the tier product's `stripe_metadata_id` (mode-agnostic handle).
 *  The create-blood-checkout edge function resolves this to the account-correct
 *  Stripe product id, so the chosen cadence works in prod AND sandbox. Mirrors
 *  `TIER_PRODUCT_ID_TO_METADATA_ID` in get-products/stripe-service.ts. */
export const CADENCE_TIER_METADATA_ID: Record<RecommendedPlan, string> = {
  baseline: "ahead-membership-base",
  twice: "ahead-membership-base-1",
  quarterly: "ahead-membership-base-3",
};

/** Cadence plan → i18n key for the "what's included" contents label
 *  ("1 advanced blood test [+ N follow-ups]"). Used by the payment option pill
 *  and the order-summary membership line so they stay in sync. */
export const CADENCE_CONTENTS_KEY: Record<RecommendedPlan, string> = {
  baseline: "paymentInfo.cadenceOnce",
  twice: "paymentInfo.cadenceTwice",
  quarterly: "paymentInfo.cadenceFour",
};

/** Fallback annual prices (CHF) when a tier's Stripe amount isn't available. */
export const MEMBERSHIP_TIER_DEFAULT_CHF: Record<string, number> = {
  once: 299,
  twice: 499,
  four: 899,
};

/** Number of follow-up tests included per cadence (1 annual baseline + N follow-ups). */
export const CADENCE_FOLLOWUPS: Record<RecommendedPlan, number> = {
  baseline: 0,
  twice: 1,
  quarterly: 3,
};

/** Standalone follow-up test price and the discounted member price (CHF), used
 *  for the "if booked separately" savings comparison. */
export const FOLLOWUP_PRICE_CHF = 250;
export const FOLLOWUP_MEMBER_PRICE_CHF = 239;

/** Annual membership price (CHF) for a cadence — real Stripe amount when present. */
export function getCadencePriceChf(
  plan: RecommendedPlan,
  tiers?: MembershipTier[],
): number {
  const tierId = CADENCE_TIER_ID[plan];
  const tier = tiers?.find((t) => t.id === tierId);
  if (tier?.annual_amount_chf != null) {
    return Math.round(tier.annual_amount_chf / 100);
  }
  return MEMBERSHIP_TIER_DEFAULT_CHF[tierId];
}

/** Price (CHF) if the same tests were booked separately: annual baseline test +
 *  standalone follow-ups. Drives the struck-through "booking separately" price. */
export function getSeparatePriceChf(
  plan: RecommendedPlan,
  tiers?: MembershipTier[],
): number {
  return (
    getCadencePriceChf("baseline", tiers) +
    CADENCE_FOLLOWUPS[plan] * FOLLOWUP_PRICE_CHF
  );
}
